  import * as S from "./style";
  import * as React from "react";
  import { theme } from "styles/theme";
  import { SubtleButton } from "components/button";
  import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

  type Tab = {
    draggable?: boolean;
    tab: React.ReactNode;
    count?: string | number | null;
    content?: React.ReactElement;
    hide?: boolean;
    unSelectable?: boolean;
    buttons?: React.ReactNode;
  };

  type Props = {
    draggable?: boolean;
    data: { [key: string]: Tab } | null;
    defaultTab?: string;
    onClick?: (key: string) => void;
    content?: React.ReactNode;
    headerOnly?: boolean;
    style?: {
      main?: {};
      tab?: {};
      content?: {};
    };
    buttons?: React.ReactNode;
    setExportType?: (key: string) => void;
    onDragEndComplete?: (newTabOrder: string[]) => void;
  };

  export default function Tab({
    data,
    defaultTab,
    onClick,
    content,
    style,
    draggable = false,
    ...props
  }: Props) {
    const [selected, setSelected] = React.useState<string | undefined>(
      defaultTab
    );
    const [tabOrder, setTabOrder] = React.useState<string[]>(data ? Object.keys(data) : []);

    const contentRender =
      !props.headerOnly &&
      ((selected && data && data[selected]?.content) || content);

      React.useEffect(() => {
        if (data) {
          const keys = Object.keys(data);
      
          // Preserve user-defined order and only append new keys
          setTabOrder((prevTabOrder) => {
            const updatedOrder = [...prevTabOrder];
      
            keys.forEach((key) => {
              if (!updatedOrder.includes(key)) {
                updatedOrder.push(key); // Append new tabs while keeping existing order
              }
            });
      
            return updatedOrder;
          });
      
          if (defaultTab) {
            setSelected(defaultTab);
          } else if (keys.length > 0) {
            const latestTab = keys[keys.length - 1]; // Get the newest tab
            setSelected(latestTab);
            onClick && onClick(latestTab); // Notify parent
          }
        }
      }, [data]); // Removed `defaultTab` to prevent unwanted resets      

    // Drag End Handler
    const handleDragEnd = (result: any) => {
      if (!result.destination) return; // If dropped outside

      const newTabOrder = Array.from(tabOrder);
      const [movedTab] = newTabOrder.splice(result.source.index, 1);
      newTabOrder.splice(result.destination.index, 0, movedTab);

      setTabOrder(newTabOrder);

      props.onDragEndComplete && props.onDragEndComplete(newTabOrder);
    };

    const populateTabs = (providedDroppable: any) =>
      tabOrder.map((key, index) => {
        const value = data?.[key];
        if (!value || value.hide) return null;

        if (value.unSelectable) {
          return value.tab;
        }

        const handleClick = (key: string) => {
          setSelected(key);
          props.setExportType && props.setExportType(key);
          onClick && onClick(key);
        };

        const isSelected = selected === key;

        return (
          <Draggable key={key} draggableId={key} index={index}>
            {(providedDraggable) => (
              <S.TabButton
                ref={providedDraggable.innerRef}
                {...providedDraggable.draggableProps}
                {...providedDraggable.dragHandleProps}
                key={key}
                isSelected={isSelected}
                onClick={() => handleClick(key)}
                className=""
              >
                <SubtleButton
                  className={`!p-0 !px-3 ${isSelected && "!text-[#ff4f00]"}`}
                >
                  {value.tab}
                  {(value.count || value.count === 0) && (
                    <span
                      style={{
                        color: theme.color.main,
                        fontSize: "0.8rem",
                        marginLeft: "0.4rem",
                      }}
                    >
                      {value.count}
                    </span>
                  )}
                </SubtleButton>
                {value.buttons || null}
              </S.TabButton>
            )}
          </Draggable>
        );
      });

    return (
      <S.Con style={{ ...style?.main }}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="tabs" direction="horizontal">
            {(providedDroppable) => (
              <S.TabCon
                ref={providedDroppable.innerRef}
                {...providedDroppable.droppableProps}
                style={{ ...style?.tab }}
                className={`${props.headerOnly && "!border-b-0"}`}
              >
                {populateTabs(providedDroppable)}
                {providedDroppable.placeholder}
                {props.buttons && <div className="ml-auto mt-0.5">{props.buttons}</div>}
              </S.TabCon>
            )}
          </Droppable>
        </DragDropContext>

        {contentRender && (
          <S.ContentCon style={{ ...style?.content }}>
            {contentRender}
          </S.ContentCon>
        )}
      </S.Con>
    );
  }
